import { Components } from '@mui/material';
import { Theme } from '@mui/material/styles';

type CheckboxVisualState = {
  borderColor: string;
  backgroundColor: string;
  iconColor?: string;
  opacity?: number;
  outline?: string;
  outlineOffset?: string;
};

type CheckboxStateStyles = {
  default: CheckboxVisualState;
  hover: CheckboxVisualState;
  focus: CheckboxVisualState;
  checked: CheckboxVisualState;
  checkedHover: CheckboxVisualState;
  disabled: CheckboxVisualState;
  disabledChecked: CheckboxVisualState;
};

const getCheckboxStates = (theme: Theme): CheckboxStateStyles => ({
  default: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
  },
  hover: {
    borderColor: theme.palette.border.primaryHover,
    backgroundColor: theme.palette.background.light,
  },
  focus: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
    outline: `2px solid ${theme.palette.border.focus}`,
    outlineOffset: '0',
  },
  checked: {
    borderColor: theme.palette.background.primaryDefault,
    backgroundColor: theme.palette.background.primaryDefault,
    iconColor: theme.palette.text.light,
  },
  checkedHover: {
    borderColor: theme.palette.background.primaryHover,
    backgroundColor: theme.palette.background.primaryHover,
    iconColor: theme.palette.text.light,
  },
  disabled: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
    opacity: theme.palette.action.disabledOpacity,
  },
  disabledChecked: {
    borderColor: theme.palette.background.primaryDefault,
    backgroundColor: theme.palette.background.primaryDefault,
    iconColor: theme.palette.text.light,
    opacity: theme.palette.action.disabledOpacity,
  },
});

const getCheckboxStateStyles = (states: CheckboxStateStyles) => ({
  border: `1px solid ${states.default.borderColor}`,
  backgroundColor: states.default.backgroundColor,
  color: states.checked.iconColor,

  '& .icon-svg': {
    color: 'currentColor',
    width: '100%',
    height: '100%',
  },

  '& .MuiSvgIcon-root': {
    display: 'none',
  },

  '&[data-preview-state="default"]': {
    borderColor: states.default.borderColor,
    backgroundColor: states.default.backgroundColor,
  },

  '&:hover, &[data-preview-state="hover"]': {
    borderColor: states.hover.borderColor,
    backgroundColor: states.hover.backgroundColor,
  },

  '&.Mui-focusVisible, &[data-preview-state="focus"]': {
    borderColor: states.focus.borderColor,
    backgroundColor: states.focus.backgroundColor,
    outline: states.focus.outline,
    outlineOffset: states.focus.outlineOffset,
  },

  '&.Mui-checked, &.MuiCheckbox-indeterminate, &[data-preview-state="checked"], &[data-preview-state="partial"]': {
    borderColor: states.checked.borderColor,
    backgroundColor: states.checked.backgroundColor,
    color: states.checked.iconColor,
  },

  '&.Mui-checked:hover, &.MuiCheckbox-indeterminate:hover, &[data-preview-state="checked-hover"], &[data-preview-state="partial-hover"]': {
    borderColor: states.checkedHover.borderColor,
    backgroundColor: states.checkedHover.backgroundColor,
    color: states.checkedHover.iconColor,
  },

  '&.Mui-disabled': {
    borderColor: states.disabled.borderColor,
    backgroundColor: states.disabled.backgroundColor,
    opacity: states.disabled.opacity,
  },

  '&.Mui-disabled.Mui-checked, &.Mui-disabled.MuiCheckbox-indeterminate, &[data-preview-state="disabled-checked"], &[data-preview-state="disabled-partial"]': {
    borderColor: states.disabledChecked.borderColor,
    backgroundColor: states.disabledChecked.backgroundColor,
    color: states.disabledChecked.iconColor,
    opacity: states.disabledChecked.opacity,
  },
});

export const getCheckboxComponents = (
  theme: Theme,
): Pick<Components, 'MuiCheckbox'> => {
  const checkboxStates = getCheckboxStates(theme);

  return {
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
          flexShrink: 0,
          position: 'relative',
          width: '24px',
          height: '24px',
          padding: 0,
          borderRadius: theme.lelyBorderRadius.SM,
          lineHeight: 1,

          '& .MuiTouchRipple-root': {
            display: 'none',
          },

          ...getCheckboxStateStyles(checkboxStates),
        },
      },
    },
  };
};